import React, { useMemo, useRef, useState } from 'react';
import { diff } from 'deep-object-diff';
import { Drawer } from 'antd';
import AddCoachesForm from './addCoachesForm';
import './coaches.css';
import useTeams from '@hooks/useTeams';
import MaterialTable, { TableActions } from '@components/molecules/MaterialTable';
import { Column, Options, SvgIconComponent } from '@material-table/core';
import FAB from '@atoms/FAB';
import { STATUS, TeamAthlete } from '@type/db-types';
import { toast } from 'react-toastify';
import { TableActionData } from '@type/mui-types';
import { updateUserAndMembership } from '@modules/supabase/users';
import invariant from 'tiny-invariant';
import { stringFromError } from '@utils/utils';
import { log } from '@utils/logging';
import { Grid2 as Grid, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import useUser from '@hooks/useUser';
import { updateTeamStatus } from '@modules/supabase/teams';
import { editableUserColumns } from '@utils/user-table-utils';

export default function Coaches() {
    const currentUser = useUser();
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();
    const { coaches, refreshMembers, selectedTeam } = useTeams();
    const [editingUser, setEditingUser] = useState<boolean | undefined>(undefined);

    const tableColumns = useMemo(
        () => editableUserColumns({ userKey: 'id' }) as unknown as Column<TeamAthlete>[],
        [],
    );

    const onSuccess = () => {
        setEditingUser(undefined);

        // takes a minute for the updated info to trickle through
        // TODO: eventually we should hook this up to a watcher...
        toast.info('Updating coaches...', { autoClose: 1000 });
        setTimeout(() => refreshMembers(), 1000);
    };

    const actions: TableActions<TeamAthlete> = useMemo(
        () => [
            {
                icon: RefreshIcon as SvgIconComponent,
                tooltip: 'Refresh Data',
                isFreeAction: true,
                onClick: refreshMembers,
            },
        ],

        [refreshMembers],
    );

    const onRowUpdate = async (newData: TeamAthlete, oldData?: TeamAthlete | undefined) => {
        const updatedFields: Partial<TeamAthlete> & TableActionData = diff(oldData || {}, newData);
        if ('tableData' in updatedFields) delete updatedFields.tableData;

        if (Object.keys(updatedFields).length) {
            invariant(selectedTeam, 'Team must be selected to add a coach');

            const user: Partial<Omit<TeamAthlete, 'weight'>> = {
                ...oldData,
                ...updatedFields,
            };
            await updateUserAndMembership(user).catch((e) =>
                log(`[Coaches:edit] ${stringFromError(e)}`),
            );
            onSuccess();
        } else {
            toast.info('No changes detected');
            return Promise.resolve();
        }
    };

    const onRowDelete = async (oldData: TeamAthlete) => {
        if (currentUser.user?.id === oldData.userId) {
            return toast.error('You cannot remove yourself as a coach');
        }
        await toast
            .promise(updateTeamStatus(STATUS.REMOVED, oldData.membershipId), {
                pending: 'Removing coach...',
                success: 'Coach removed',
                error: 'Error removing coach',
            })
            .then(refreshMembers);
    };

    // if no team selected, show a message
    if (!selectedTeam) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: '90vh', width: '100%' }}
            >
                <Grid>
                    <Typography variant="h5">Please select a team to view coaches</Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            <MaterialTable<TeamAthlete>
                tableRef={tableRef}
                actions={actions}
                columns={tableColumns}
                data={coaches || []}
                editable={{
                    isEditable: () => true,
                    onRowUpdate,
                    onRowDelete,
                }}
                localization={{
                    toolbar: {
                        nRowsSelected: (number) =>
                            `${number} coach${number === 1 ? '' : 'es'} selected`,
                    },
                }}
                loading={coaches === undefined}
                options={TABLE_OPTIONS}
                style={{ boxShadow: 'none' }}
                // onSelectionChange={onSelectionChange}
                // onRowClick={(e, rowData) => {
                //     setSelecteduser(rowData);
                // }}
                tableId="BrandBadges"
                title="Coaches"
            />
            <Drawer
                title={typeof editingUser === 'boolean' ? 'Add coach' : 'Edit Coach'}
                placement="right"
                onClose={() => setEditingUser(undefined)}
                open={editingUser !== undefined}
                width={'550px'}
                destroyOnClose={true}
            >
                <AddCoachesForm editingUser={editingUser} onSuccess={onSuccess} />
            </Drawer>
            <FAB onClick={() => setEditingUser(true)} toolTip="Add coach to team" />
        </>
    );
}

const TABLE_OPTIONS: Options<TeamAthlete> = {
    actionsColumnIndex: -1,
    // maxBodyHeight: `${materialTableHeight + 3}vh`,
    // minBodyHeight: `${materialTableHeight + 3}vh`,
};
